import { FocusEvent, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import {
    fetchVoicemailSettings,
    updateVoicemailSettings,
} from 'flow/actions/voicemail-actions';
import NumberFieldSmart from 'flow/components/custom-components/NumberFieldSmart';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useCustomer from 'flow/hooks/useCustomer';
import useVoicemail from 'flow/hooks/useVoicemail';
import { routes } from 'flow/types/routes';

import { getCountryCode } from 'flow/utils/extension-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

const SettingsVoicemailAlternatives = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { contact } = useContact(extensionKey);
    const { ownCustomer } = useCustomer(extensionKey);
    const { voicemailSettings } = useVoicemail(extensionKey);

    const [colleague, setColleague] = useState<string>(undefined);
    const [operator, setOperator] = useState<string>(undefined);

    const spokenLanguage = useAppSelector((state) =>
        state.app.get('spokenLanguage'),
    );

    useEffect(() => {
        if (!voicemailSettings) {
            dispatch(fetchVoicemailSettings(extensionKey));
        }
    }, [voicemailSettings, extensionKey]);

    if (!voicemailSettings) {
        return <Loading />;
    }

    const handleUpdate = (updatedSettings: {
        key: string;
        leaveMessage?: boolean;
        hold?: boolean;
        operator?: boolean;
        operatorNumberDTO?: { number: string };
        colleague?: boolean;
        colleagueNumberDTO?: { number: string };
    }) => {
        dispatch(updateVoicemailSettings(extensionKey, updatedSettings));
    };

    const getDescription = (
        redialType: 'landline' | 'mobile_and_landline' | 'everywhere' | 'none',
    ) =>
        i18next.exists(`settings.voicemail_alt.${redialType}`)
            ? i18next.t(`settings.voicemail_alt.${redialType}`)
            : i18next.t('option.none');

    const setColleagueNumber = (event: FocusEvent<HTMLFormElement>) => {
        event.preventDefault();

        const colleagueNumber = voicemailSettings.getIn([
            'colleagueNumberDTO',
            'number',
        ]);

        if (![undefined, colleagueNumber].includes(colleague)) {
            const updatedSettings = {
                key: voicemailSettings.get('key'),
                colleagueNumberDTO: {
                    number: colleague,
                },
            };

            handleUpdate(updatedSettings);
        }
    };

    const setOperatorNumber = (event: FocusEvent<HTMLFormElement>) => {
        event.preventDefault();

        const operatorNumber = voicemailSettings.getIn([
            'operatorNumberDTO',
            'number',
        ]);

        if (![undefined, operatorNumber].includes(operator)) {
            const updatedSettings = {
                key: voicemailSettings.get('key'),
                operatorNumberDTO: {
                    number: operator,
                },
            };

            handleUpdate(updatedSettings);
        }
    };

    return (
        <Page
            header="settings.voicemail_alternatives"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.voicemail'], {
                        extensionKey,
                    }),
                )
            }
        >
            <SettingRow
                cta={
                    <Toggle
                        value={voicemailSettings.get('leaveMessage')}
                        onChange={(value: boolean) =>
                            handleUpdate({
                                key: voicemailSettings.get('key'),
                                leaveMessage: value,
                            })
                        }
                        text={i18next.t('settings.voicemail_alt.leave_message')}
                    />
                }
            >
                {i18next.t('settings.voicemail_alt.leave_message')}
            </SettingRow>

            <div className="px-2 text-sm text-gray-600">
                {i18next.t('settings.voicemail_alt.message') + ': '}
                <strong>
                    {`"${i18next.t('settings.voicemail_alt.press_1', {
                        lng: spokenLanguage,
                    })}"`}
                </strong>
            </div>
            <Divider />

            <SettingRow
                cta={
                    <Toggle
                        value={voicemailSettings.get('hold')}
                        onChange={(value: boolean) =>
                            handleUpdate({
                                key: voicemailSettings.get('key'),
                                hold: value,
                            })
                        }
                        text={i18next.t('settings.voicemail_alt.stay_on')}
                    />
                }
            >
                {i18next.t('settings.voicemail_alt.stay_on')}
            </SettingRow>

            <div className="px-2 text-sm text-gray-600">
                {i18next.t('settings.voicemail_alt.message') + ': '}
                <strong>
                    {'"' +
                        i18next.t('settings.voicemail_alt.press_2', {
                            lng: spokenLanguage,
                        }) +
                        '"'}
                </strong>
            </div>
            <Divider />

            <RouteLink
                to={routes['settings.voicemail.alternatives.redialtype']}
                description={getDescription(
                    voicemailSettings.get('redialType'),
                )}
            >
                {i18next.t('settings.voicemail_alt.permit_callback_to')}
            </RouteLink>
            <div className="px-2 text-sm text-gray-600">
                {i18next.t('settings.voicemail_alt.message') + ': '}
                <strong>
                    {'"' +
                        i18next.t('settings.voicemail_alt.press_3', {
                            lng: spokenLanguage,
                        }) +
                        '"'}
                </strong>
                <br />
                {i18next.t('settings.voicemail_alt.only_when_busy')}
            </div>
            <Divider />

            <SettingRow
                cta={
                    <Toggle
                        value={voicemailSettings.get('colleague')}
                        onChange={(value: boolean) =>
                            handleUpdate({
                                key: voicemailSettings.get('key'),
                                colleague: value,
                            })
                        }
                        text={i18next.t(
                            'settings.voicemail_alt.connect_to_colleague',
                        )}
                    />
                }
            >
                {i18next.t('settings.voicemail_alt.connect_to_colleague')}
            </SettingRow>

            <div className="px-2 text-sm text-gray-600">
                {i18next.t('settings.voicemail_alt.message') + ': '}
                <strong>
                    {'"' +
                        i18next.t('settings.voicemail_alt.press_7', {
                            lng: spokenLanguage,
                        }) +
                        '"'}
                </strong>
            </div>

            {voicemailSettings.get('colleague') && (
                <>
                    <SettingSection className="mt-2">
                        {i18next.t(
                            'settings.voicemail_alt.number_to_colleague',
                        )}
                    </SettingSection>

                    <form
                        onBlur={setColleagueNumber}
                        onSubmit={setColleagueNumber}
                        className="px-2"
                    >
                        <NumberFieldSmart
                            id="phoneNumber"
                            name="phoneNumber"
                            numberDTO={voicemailSettings.get(
                                'colleagueNumberDTO',
                            )}
                            disabled={!voicemailSettings.has('colleague')}
                            defaultCountryCode={getCountryCode(
                                contact,
                                ownCustomer,
                            )}
                            onChange={(_: string, phone: string) =>
                                setColleague(phone)
                            }
                        />
                    </form>
                </>
            )}
            <Divider />

            <SettingRow
                cta={
                    <Toggle
                        value={voicemailSettings.get('operator')}
                        onChange={(value: boolean) =>
                            handleUpdate({
                                key: voicemailSettings.get('key'),
                                operator: value,
                            })
                        }
                        text={i18next.t(
                            'settings.voicemail_alt.connect_to_operator',
                        )}
                    />
                }
            >
                {i18next.t('settings.voicemail_alt.connect_to_operator')}
            </SettingRow>

            <div className="px-2 text-sm text-gray-600">
                {i18next.t('settings.voicemail_alt.message') + ': '}
                <strong>
                    {'"' +
                        i18next.t('settings.voicemail_alt.press_9', {
                            lng: spokenLanguage,
                        }) +
                        '"'}
                </strong>
            </div>

            {voicemailSettings.get('operator') && (
                <>
                    <SettingSection className="mt-2">
                        {i18next.t('settings.voicemail_alt.number_to_operator')}
                    </SettingSection>

                    <form
                        onBlur={setOperatorNumber}
                        onSubmit={setOperatorNumber}
                        className="px-2"
                    >
                        <NumberFieldSmart
                            id="phoneNumber"
                            name="phoneNumber"
                            numberDTO={voicemailSettings.get(
                                'operatorNumberDTO',
                            )}
                            disabled={!voicemailSettings.has('operator')}
                            defaultCountryCode={getCountryCode(
                                contact,
                                ownCustomer,
                            )}
                            onChange={(_: string, phone: string) =>
                                setOperator(phone)
                            }
                        />
                    </form>
                </>
            )}
        </Page>
    );
};

export default SettingsVoicemailAlternatives;
